import posthog from 'posthog-js';

// Initialize PostHog
export const initPostHog = () => {
  const apiKey = import.meta.env.VITE_POSTHOG_API_KEY;
  const host = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';

  if (!apiKey) {
    console.warn('PostHog API key not found. Analytics will not be tracked.');
    return;
  }

  posthog.init(apiKey, {
    api_host: host,
    autocapture: false, // We'll manually track events for better control
    capture_pageview: false, // We'll manually track page views
    capture_pageleave: true, // Auto-track time on page
  });
};

// Helper to check if PostHog is ready
const isPostHogReady = (): boolean => {
  try {
    return posthog && typeof posthog.capture === 'function';
  } catch {
    return false;
  }
};

// Track page view events
export const trackPageView = (
  pageName: string,
  properties?: Record<string, any>
) => {
  if (!isPostHogReady()) {
    console.warn('PostHog not ready for trackPageView');
    return;
  }
  
  posthog.capture('$pageview', {
    $current_url: window.location.href,
    page_name: pageName,
    ...properties,
  });
};

// Track custom events
export const trackEvent = (
  eventName: string,
  properties?: Record<string, any>
) => {
  if (!isPostHogReady()) {
    console.warn('PostHog not ready for trackEvent:', eventName);
    return;
  }
  
  posthog.capture(eventName, properties);
};

// Identify user (call on login)
export const identifyUser = (
  userId: string,
  userProperties?: Record<string, any>
) => {
  if (!isPostHogReady()) {
    console.warn('PostHog not ready for identifyUser');
    return;
  }
  
  posthog.identify(userId, userProperties);
};

// Reset user (call on logout)
export const resetUser = () => {
  if (!isPostHogReady()) {
    console.warn('PostHog not ready for resetUser');
    return;
  }
  
  posthog.reset();
};

// Set user properties
export const setUserProperties = (properties: Record<string, any>) => {
  if (!isPostHogReady()) {
    console.warn('PostHog not ready for setUserProperties');
    return;
  }
  
  posthog.setPersonProperties(properties);
};

// Archive-specific tracking functions
export const trackArchivePageView = (properties?: Record<string, any>) => {
  if (!isPostHogReady()) {
    console.warn('PostHog not ready for trackArchivePageView');
    return;
  }
  
  console.log('Tracking archive page view:', properties);
  posthog.capture('my_archive_page_viewed', {
    page_type: 'my_archive',
    page_path: '/account/portfolio',
    ...properties,
  });
};

export const trackArchiveCreated = (archiveData: {
  archiveId: string;
  itemType?: string;
  category?: string;
  hasImage?: boolean;
  properties?: Record<string, any>;
}) => {
  if (!isPostHogReady()) {
    console.warn('PostHog not ready for trackArchiveCreated');
    return;
  }
  
  console.log('Tracking archive created:', archiveData);
  posthog.capture('archive_created', {
    archive_id: archiveData.archiveId,
    item_type: archiveData.itemType,
    category: archiveData.category,
    has_image: archiveData.hasImage,
    timestamp: new Date().toISOString(),
    ...archiveData.properties,
  });
};

export const trackArchiveDeleted = (archiveId: string, properties?: Record<string, any>) => {
  if (!isPostHogReady()) {
    console.warn('PostHog not ready for trackArchiveDeleted');
    return;
  }
  
  console.log('Tracking archive deleted:', archiveId, properties);
  posthog.capture('archive_deleted', {
    archive_id: archiveId,
    ...properties,
  });
};

export const trackArchiveUpdated = (archiveId: string, properties?: Record<string, any>) => {
  if (!isPostHogReady()) {
    console.warn('PostHog not ready for trackArchiveUpdated');
    return;
  }
  
  console.log('Tracking archive updated:', archiveId, properties);
  posthog.capture('archive_updated', {
    archive_id: archiveId,
    ...properties,
  });
};

export default posthog;
